import { findClosestSampleIndex } from './splitTimes';

// Absolute gradient: Blue (min) → Green (max)
const ABSOLUTE_STOPS = [
  { t: 0.0, r: 33, g: 150, b: 243 },   // #2196F3 blue
  { t: 1.0, r: 46, g: 204, b: 113 },   // #2ecc71 green
];

// Delta gradient: Green → White → Red (symmetric around 0)
const DELTA_STOPS = [
  { t: 0.0, r: 39, g: 174, b: 96 },    // #27ae60 green (ref faster)
  { t: 0.5, r: 255, g: 255, b: 255 },   // #ffffff white (equal)
  { t: 1.0, r: 231, g: 76, b: 60 },     // #e74c3c red (cmp faster)
];

function interpolateGradient(stops, t) {
  const clamped = Math.max(0, Math.min(1, t));
  let lower = stops[0];
  let upper = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (clamped >= stops[i].t && clamped <= stops[i + 1].t) {
      lower = stops[i];
      upper = stops[i + 1];
      break;
    }
  }
  const span = upper.t - lower.t || 1;
  const f = (clamped - lower.t) / span;
  const r = Math.round(lower.r + (upper.r - lower.r) * f);
  const g = Math.round(lower.g + (upper.g - lower.g) * f);
  const b = Math.round(lower.b + (upper.b - lower.b) * f);
  return `rgb(${r},${g},${b})`;
}

export function speedToColor(value, min, max) {
  const range = max - min || 1;
  const t = (value - min) / range;
  return interpolateGradient(ABSOLUTE_STOPS, t);
}

export function deltaToColor(delta, maxAbsDelta) {
  const cap = maxAbsDelta || 1;
  // delta > 0 means ref lap faster → green (t=0), delta < 0 means cmp faster → red (t=1)
  const t = 0.5 - (delta / cap) * 0.5;
  return interpolateGradient(DELTA_STOPS, t);
}

/**
 * Build per-coordinate heatmap colors from telemetry data.
 *
 * @param {Array} coordinates - track GPS points with { lat, lng }
 * @param {Array} laps - selected lap objects with .data[] telemetry
 * @returns {{ colors: string[], mode: string, min: number, max: number, label: string }}
 */
export function buildHeatmapColors(coordinates, laps) {
  if (!coordinates?.length || !laps?.length) return null;

  const isDelta = laps.length === 2;
  // For 3+ laps, use the best (fastest) lap
  const primaryLap = laps.length > 2
    ? laps.reduce((best, lap) => (!best || (lap.lapTime || Infinity) < (best.lapTime || Infinity)) ? lap : best, null)
    : laps[0];

  if (!primaryLap?.data?.length) return null;

  const values = [];

  for (let i = 0; i < coordinates.length; i++) {
    const coord = coordinates[i];
    if (coord.lat == null || coord.lng == null) {
      values.push(null);
      continue;
    }

    const match1 = findClosestSampleIndex(primaryLap.data, coord.lat, coord.lng);
    if (match1.index < 0) {
      values.push(null);
      continue;
    }
    const speed1 = primaryLap.data[match1.index].speed || 0;

    if (isDelta) {
      const lap2 = laps[1];
      if (!lap2?.data?.length) {
        values.push(null);
        continue;
      }
      const match2 = findClosestSampleIndex(lap2.data, coord.lat, coord.lng);
      if (match2.index < 0) {
        values.push(null);
        continue;
      }
      const speed2 = lap2.data[match2.index].speed || 0;
      values.push(speed1 - speed2); // positive = lap1 faster
    } else {
      values.push(speed1);
    }
  }

  const validValues = values.filter((v) => v != null);
  if (!validValues.length) return null;

  let colors;
  let min, max, label;

  if (isDelta) {
    const maxAbsDelta = Math.max(
      Math.abs(Math.min(...validValues)),
      Math.abs(Math.max(...validValues)),
      1
    );
    min = -maxAbsDelta;
    max = maxAbsDelta;
    label = 'Speed Delta (MPH)';
    colors = values.map((v) => v != null ? deltaToColor(v, maxAbsDelta) : null);
  } else {
    min = Math.min(...validValues);
    max = Math.max(...validValues);
    label = 'Speed (MPH)';
    colors = values.map((v) => v != null ? speedToColor(v, min, max) : null);
  }

  return { colors, mode: isDelta ? 'delta' : 'absolute', min, max, label };
}

/**
 * Generate CSS gradient string for the legend bar.
 */
export function getLegendGradientCSS(mode) {
  const stops = mode === 'delta' ? DELTA_STOPS : ABSOLUTE_STOPS;
  const cssStops = stops.map((s) => `rgb(${s.r},${s.g},${s.b}) ${s.t * 100}%`).join(', ');
  return `linear-gradient(to right, ${cssStops})`;
}
